// index.tsx
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MultiStepForm, { FormStep } from '@/components/advanced/forms/MultiStepForm';
import { toast } from '@/components/ui/providers/AlertProvider';
import {
  useMemberService,
  useCareNeedService,
  useCareZoneService,
  useCareAreaService,
  useLocationService,
  useLeaderService,
  useACCounsellingServices,
  useMemberTypeService,
  CareCase,
   CreateCareCase,
   Member,
   MemberType
} from '@actschurch/ccms-sdk';

import type { member, StagedCounselee, WizardState } from './types';
import Step1Counselees from './Steps/Step2Counselees';
import Step2Need from './Steps/Step1Need';
import Step3Assignment from '../CreateCaseWizard/Steps/Step3Assignment';
import Step4Details from '../CreateCaseWizard/Steps/Step4Details';
import Step5Review from '../CreateCaseWizard/Steps/Step5Review';

interface CreateCaseWizardProps {
  onCancel?: () => void;
  onCreated?: (caseID: number) => void;
}

export default function CreateCaseWizard({ onCancel, onCreated }: CreateCaseWizardProps) {
  const navigate = useNavigate();
  const memberTypeService = useMemberTypeService();

  const memberService     = useMemberService();
  const CareNeeds          = useCareNeedService();
  const CareZones          = useCareZoneService();
  const CareAreas          = useCareAreaService();
  const Locations          = useLocationService();
  const Leaders            = useLeaderService();
  const counsellingService = useACCounsellingServices();

  const [loading, setLoading] = useState(false);

  // ── Step 1: Counselees ────────────────────────────────────────────────
  const [search, setSearch]       = useState('');
  const [results, setResults]     = useState<Member[]>([]);
  const [searching, setSearching] = useState(false);
  const [staged, setStaged]       = useState<StagedCounselee[]>([]);

    // ── Member types (for create-new-member quick-add) ──────────────────────
  const [allMemberTypes, setAllMemberTypes] = useState<MemberType[]>([]);
  const [creatingMember, setCreatingMember] = useState(false);

  useEffect(() => {
    const term = search.trim();
    if (!term) { setResults([]); return; }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const res: any = await memberService.searchMembers(term);
        setResults(res?.data ?? res ?? []);
      } catch (e) {
        console.error(e);
        toast.error('Error', 'Could not search members.');
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [search, memberService]);

  useEffect(() => {
    memberTypeService.getAll()
      .then((res: any) => setAllMemberTypes(res?.data ?? res ?? []))
      .catch(console.error);
  }, [memberTypeService]);
  
   const isStaged = (m: Member) => staged.some((s) => s.member.memberID === m.memberID);

  const addCounselee = (m: Member, isMain: boolean) => {
    setStaged((prev) => {
      const next = isMain ? prev.map((s) => ({ ...s, isMain: false })) : prev;
      return [...next, { member: m, isMain }];
    });
  };

  const removeCounselee = (memberID: number) => {
    setStaged((prev) => prev.filter((s) => s.member.memberID !== memberID));
  };

  const setMain = (memberID: number) => {
    setStaged((prev) => prev.map((s) => ({ ...s, isMain: s.member.memberID === memberID })));
  };

  // ── Create a brand-new member when search comes up empty ────────────────
  const createMember = async (payload: {
    firstName: string;
    lastName: string;
    email: string;
    cellNumber?: string | null;
    typeID: number;
  }) => {
    setCreatingMember(true);
    try {
      const newMember: Partial<Member> = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        cellNumber: payload.cellNumber ?? null,
        typeID: payload.typeID,
        isMember: false,
        isActive: true,
      };

      const res: any = await memberService.create(newMember as Member);
      const created: Member = res?.data ?? res;

      addCounselee(created, staged.length === 0);
      setSearch('');
      setResults([]);
      toast.success('Member Created', `${payload.firstName} ${payload.lastName} was added.`);
      return created;
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to create member.');
      return null;
    } finally {
      setCreatingMember(false);
    }
  };
 
  // ── Step 2: Need ─────────────────────────────────────────────────────
  const [allNeeds, setAllNeeds] = useState<any[]>([]);
  const [needID, setNeedID]     = useState<number | null>(null);

  useEffect(() => {
    CareNeeds.getAll().then(setAllNeeds).catch(console.error);
  }, [CareNeeds]);

  // ── Step 3: Location > Zone > Area > Leader ─────────────────────────
  const [allLocations, setAllLocations] = useState<any[]>([]);
  const [allZones, setAllZones]         = useState<any[]>([]);
  const [allAreas, setAllAreas]         = useState<any[]>([]);
  const [allLeaders, setAllLeaders]     = useState<any[]>([]);

  const [locationID, setLocationID]     = useState<number | null>(null);
  const [zoneID, setZoneID]             = useState<number | null>(null);
  const [areaID, setAreaID]             = useState<number | null>(null);
  const [leaderUserId, setLeaderUserId] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      Locations.getAll(),
      CareZones.getAll(),
      CareAreas.getAll(),
      Leaders.list(),
    ]).then(([locs, zones, areas, leaders]) => {
      setAllLocations(locs ?? []);
      setAllZones(zones ?? []);
      setAllAreas(areas ?? []);
      setAllLeaders(leaders ?? []);
    }).catch(console.error);
  }, [Locations, CareZones, CareAreas, Leaders]);

  const handleLocationChange = (val: string | null) => {
    setLocationID(val ? Number(val) : null);
    setZoneID(null); setAreaID(null); setLeaderUserId(null);
  };
  const handleZoneChange = (val: string | null) => {
    setZoneID(val ? Number(val) : null);
    setAreaID(null); setLeaderUserId(null);
  };
  const handleAreaChange = (val: string | null) => {
    setAreaID(val ? Number(val) : null);
    setLeaderUserId(null);
  };

  // ── Step 4: Name + Description ──────────────────────────────────────
  const [caseName, setCaseName]       = useState('');
  const [description, setDescription] = useState('');

  // ── Bundle shared state/handlers for step components ────────────────
  const wizardState: WizardState = {
    search, setSearch, results, searching, staged,
    addCounselee, removeCounselee, setMain, isStaged,
    allMemberTypes, creatingMember, createMember,   // NEW
    allNeeds, needID, setNeedID,
    allLocations, allZones, allAreas, allLeaders,
    locationID, zoneID, areaID, leaderUserId,
    handleLocationChange, handleZoneChange, handleAreaChange, setLeaderUserId,
    caseName, setCaseName, description, setDescription,
  };

  // ── Validation per step ─────────────────────────────────────────────
  const validateStep1 = () => {
    if (staged.length === 0) {
      toast.error('Missing Counselees', 'Add at least one counselee to continue.');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!needID) {
      toast.error('Missing Need', 'Please select a need.');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!leaderUserId) {
      toast.error('Missing Leader', 'Please select a leader to complete this step.');
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    if (!caseName.trim()) {
      toast.error('Missing Case Name', 'Please enter a case name.');
      return false;
    }
    return true;
  };

 const handleSubmit = async () => {
  setLoading(true);
  try {


    const payload: CreateCareCase = {
      name: caseName,
      description: description ?? null,
      needID: needID!,       // validated in Step 2 before this step is reachable
      userId: leaderUserId,
      locationID,
      zoneID,
      areaID,
      memberIDs: staged.map((s) => s.member.memberID),
    };

    const created: any = await counsellingService.careCase.create(payload);
    const newCaseID = created?.data?.caseID ?? created?.caseID;

    const mainCounselee = staged.find((s) => s.isMain);
    if (newCaseID && mainCounselee) {
      await counsellingService.careCase.addMember(
        newCaseID,
        mainCounselee.member.memberID,
        true
      );
    }

    toast.success('Case Created', `${caseName} has been created successfully.`);
    onCreated?.(newCaseID);
    if (newCaseID) navigate(`/Counselling/Cases/${newCaseID}`);
  } catch (e) {
    console.error('Error creating case:', e);
    toast.error('Error', 'Failed to create case. Please try again.');
  } finally {
    setLoading(false);
  }
};

  // ── Steps ─────────────────────────────────────────────────────────
  const steps: FormStep[] = useMemo(() => [
    {
       title: 'Need',
      description: 'What kind of need',
      validate: validateStep2,
      content: <Step2Need {...wizardState} />,
    
    },
    {
       title: 'Counselees',
      description: 'Search and add',
      validate: validateStep1,
      content: <Step1Counselees {...wizardState} />,
    },
    {
      title: 'Assignment',
      description: 'Location, Zone, Area, Leader',
      validate: validateStep3,
      content: <Step3Assignment {...wizardState} />,
    },
    {
      title: 'Details',
      description: 'Name & description',
      validate: validateStep4,
      content: <Step4Details {...wizardState} />,
    },
    {
      title: 'Review',
      description: 'Confirm & submit',
      content: <Step5Review {...wizardState} />,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [wizardState]);

  return (
    <MultiStepForm
      steps={steps}
      onComplete={handleSubmit}
      onCancel={onCancel}
      completeLabel="Create Case"
      loading={loading}
      allowSkip={false}
    />
  );
}